/**
 * Royal Lady — silent session refresh + multi-tab sync.
 *
 * Attached to the shared axios instance from `lib/api.js` so every API call
 * automatically benefits from:
 *
 *   1. Silent access-token refresh on 401. A single shared refresh promise
 *      is used so N concurrent 401s only trigger ONE `/api/auth/refresh`
 *      call — no thundering herd, no race conditions.
 *
 *   2. Multi-tab authentication sync via BroadcastChannel + the `storage`
 *      event. When one tab refreshes, every other tab picks up the new
 *      access token in the same tick, so a checkout in Tab B doesn't have
 *      to hit `/refresh` itself.
 *
 *   3. Graceful expiry handling. If the refresh itself returns 401 (the
 *      refresh token itself expired or was revoked), the user is redirected
 *      to /login WITH a `?next=` query param so we land them back on the
 *      exact page they were on.
 *
 * Zero visible UX side-effects — no toast, no reload, no interruption to
 * checkout.
 */

import { API_BASE, TOKEN_KEY } from "./apiConstants";

const REFRESH_URL = `${API_BASE}/auth/refresh`;

// Endpoints where a 401 must NOT trigger silent refresh — they are the
// refresh flow itself or the initial credential exchange.
const NO_REFRESH_PATHS = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/otp/"];

// ------------------------------ multi-tab

const CHANNEL_NAME = "royal-lady-auth";
let bc = null;
try {
  if (typeof BroadcastChannel !== "undefined") bc = new BroadcastChannel(CHANNEL_NAME);
} catch (_) { /* Safari private mode etc. */ }

function broadcastTokenChange(token) {
  try { bc && bc.postMessage({ type: "token", token: token || "" }); } catch (_) {}
}

function broadcastLogout() {
  try { bc && bc.postMessage({ type: "logout" }); } catch (_) {}
}

function installMultiTabListener() {
  // BroadcastChannel — modern browsers.
  if (bc) {
    bc.onmessage = (evt) => {
      const m = evt?.data;
      if (!m) return;
      if (m.type === "token") {
        // Do not toast — silent handoff.
        if (m.token) localStorage.setItem(TOKEN_KEY, m.token);
      } else if (m.type === "logout") {
        localStorage.removeItem(TOKEN_KEY);
        // A separate tab has logged out; drop this tab to /login too so we
        // never leave a stale session hanging.
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
      }
    };
  }
  // Fallback: cross-tab notifications via the storage event (works even
  // without BroadcastChannel, e.g. Safari < 15.4).
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
      if (e.key === TOKEN_KEY && e.newValue == null && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    });
  }
}

// ------------------------------ shared refresh promise

let inflightRefresh = null;

function attemptRefresh() {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = fetch(REFRESH_URL, {
    method: "POST",
    credentials: "include",   // send the HttpOnly refresh cookie
    headers: { "Content-Type": "application/json" },
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(`refresh failed: ${r.status}`);
      const data = await r.json();
      if (!data?.token) throw new Error("refresh response missing token");
      localStorage.setItem(TOKEN_KEY, data.token);
      broadcastTokenChange(data.token);
      return data.token;
    })
    .finally(() => { inflightRefresh = null; });
  return inflightRefresh;
}

// ------------------------------ install interceptor

export function installSessionRefresh(api) {
  installMultiTabListener();

  api.interceptors.response.use(
    (r) => r,
    async (error) => {
      const status = error?.response?.status;
      const cfg = error?.config || {};
      const url = cfg.url || "";
      const isRefreshable =
        status === 401 &&
        !cfg._retry &&
        !NO_REFRESH_PATHS.some((p) => url.includes(p));

      if (!isRefreshable) return Promise.reject(error);

      try {
        const newToken = await attemptRefresh();
        // Retry the original request with the fresh access token.
        cfg._retry = true;
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return api.request(cfg);
      } catch (refreshErr) {
        // Refresh failed → the user is genuinely logged out.
        localStorage.removeItem(TOKEN_KEY);
        broadcastLogout();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          const next = encodeURIComponent(
            window.location.pathname + window.location.search,
          );
          // Preserve the destination so the user comes back after login.
          window.location.replace(`/login?next=${next}`);
        }
        return Promise.reject(error);
      }
    },
  );

  return api;
}

// Explicit logout helper — revokes on the server AND syncs every tab.
export async function performLogout() {
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" });
  } catch (_) { /* even if the server call fails, complete the client wipe */ }
  localStorage.removeItem(TOKEN_KEY);
  broadcastLogout();
}
